/**
 * App root.
 *
 * - Applies the resolved variant's theme class to <html> so token overrides
 *   (e.g. EIA accent) take effect app-wide.
 * - Renders the router.
 *
 * Why switching pages does not blank the screen:
 *  - `v7_startTransition` runs every navigation as a React transition, so while
 *    the next page's code downloads, the CURRENT page stays on screen instead
 *    of being swapped for a loading state. Without it, the first visit to each
 *    page replaced the whole app — app bar included — with "Loading…".
 *  - the page-level loading state lives INSIDE AppLayout (around the Outlet),
 *    so even a first load only fills the content area; the app bar and footer
 *    never disappear.
 * The Suspense here is only the last resort for the very first paint, before
 * any layout exists, and it shows NOTHING (user order 2026-10-23: no
 * "Loading…" page). main.jsx loads the first page's code before rendering at
 * all, so normally it is never reached.
 */
import { Suspense, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';

import { router } from '@router/routes';
import { useAppMode } from '@variant/useAppMode';

export default function App() {
  const { variant } = useAppMode();

  useEffect(() => {
    const el = document.documentElement;
    el.classList.add(variant.themeClass);
    el.setAttribute('data-app-mode', variant.mode);
    // main.jsx already set the tab title before the first render (index.html
    // has none); kept here so it follows the variant if it ever changes.
    if (variant.documentTitle) document.title = variant.documentTitle;
    return () => el.classList.remove(variant.themeClass);
  }, [variant.themeClass, variant.mode, variant.documentTitle]);

  return (
    <Suspense fallback={null}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </Suspense>
  );
}
